// Pit stop CONDICIONAL do fluxo MAD (PIT-01/03/04/08/13) — atrás da flag
// MAD_PITSTOP_ENABLED (ligada em staging/homolog, desligada em produção até a Fase 9;
// ver utils/constants.ts). Ports de index.tsx:2911-2919 (entrada no pit
// stop na promoção), 2920-2932 (aprovação do assessor), 2736-2745 (parada) e
// 2591-2601 (resets 4.1/5). Funções PURAS que mutam e devolvem o objeto de escrita;
// nenhum import de React (AD-002) nem de normal/ (isolamento AD-001).

import { MAD_PITSTOP_ENABLED } from 'utils/constants';

import { MadRuleContext, codEtapaComposto, isCoe, prosseguirGstComposto } from './context';
import { NextManagersV2 } from '../shared/types';

// DIAG-01 vive em succession.ts (única definição canônica, DRY). Reexportado aqui
// para que o consumidor do pit stop tenha a telemetria de cadeia inválida à mão.
export { logInvalidChain } from './succession';

// PIT-01 — decisão de ENTRAR no pit stop na promoção. As 5 condições são TODAS
// necessárias (E26/E27/E28 são as recíprocas de cada uma falhando):
//   1. flag MAD_PITSTOP_ENABLED ligada (PIT-13);
//   2. prosseguir_gst COMPOSTO pela dupla === 1 (o gestor ou o par tem prosseguir);
//   3. cod_etapa COMPOSTO e normalizado === 4 (nível de pit stop) — E26: prosseguir 1
//      sem cod4 → false;
//   4. o DONO da realização não é COE — E27;
//   5. o promotor (gestor que aprecia) não é COE — E28.
// Além disso exige nextManagers.prosseguir presente: sem ele o fluxo segue NORMAL
// (não há próximo elo de pit stop) e NÃO se loga anomalia.
//
// Fonte do ownerCoe: o flag `task.owner_coe`, projetado pelo `tasks_manager_mad` a
// partir do `tipo_dependencia` do DONO da realização — é o mesmo insumo que o
// `next_managers` usa para decidir se devolve o elo `prosseguir`, então os dois lados
// concordam por construção. Booleano do aggregate MANDA, inclusive quando é `false`.
// Só na AUSÊNCIA do campo (objeto que não veio do aggregate) caímos em
// `isCoe(original_manager_detail)` — proxy conservador e sabidamente impreciso, pois
// olha o GESTOR e não o dono; sem ele um dono COE poderia armar pit stop indevidamente.
//
// Testabilidade (opção b): a flag entra como parâmetro `enabled` com default
// MAD_PITSTOP_ENABLED — o ramo ON é exercitado passando `enabled: true`, sem jest.mock
// da constante. `ctx` compõe a assinatura por simetria com os demais módulos.
export function shouldEnterPitStop(
  ctx: MadRuleContext,
  promoter: any,
  task: any,
  nextManagers: NextManagersV2 | null | undefined,
  enabled: boolean = MAD_PITSTOP_ENABLED,
): boolean {
  void ctx;

  const ownerCoe = typeof task?.owner_coe === 'boolean' ? task.owner_coe : isCoe(task?.original_manager_detail);

  return (
    !!enabled &&
    prosseguirGstComposto(promoter) === 1 &&
    codEtapaComposto(promoter) === 4 &&
    !ownerCoe &&
    !isCoe(promoter) &&
    !!nextManagers?.prosseguir
  );
}

// Escrita da ENTRADA no pit stop na promoção — port de index.tsx:2911-2919 (+ o push
// em evaluator de 2937/2951). Marca prosseguir_gst='next', aponta o próximo assessor
// e o registra tanto em prosseguir_managers quanto em evaluator (criando os arrays se
// ausentes). Muta e devolve `updatedData`.
export function applyPitStopOnPromote(updatedData: any, nextManagers: NextManagersV2 | null | undefined): any {
  updatedData.prosseguir_gst = 'next';
  updatedData.prosseguir_idGestor = nextManagers?.prosseguir?._id;

  if (Array.isArray(updatedData.prosseguir_managers)) {
    updatedData.prosseguir_managers.push(updatedData.prosseguir_idGestor);
  } else {
    updatedData.prosseguir_managers = [updatedData.prosseguir_idGestor];
  }

  if (!Array.isArray(updatedData.evaluator)) {
    updatedData.evaluator = [];
  }

  updatedData.evaluator.push(updatedData.prosseguir_idGestor);

  // O assessor do pit stop é o gestor SEGUINTE (posicional) — não há escolha de outro
  // gestor. Grava-se o $gestor em evaluator (acima) e o $gestor_2, QUANDO HOUVER, em
  // non_evaluator, para que a dupla do assessor enxergue a realização. A dupla é opcional:
  // sem parceiro, non_evaluator não recebe nada. A saneadora (EeXkmdC) valida a integridade.
  const partnerId = nextManagers?.prosseguir_partner?._id;

  if (partnerId) {
    if (!Array.isArray(updatedData.non_evaluator)) {
      updatedData.non_evaluator = [];
    }

    updatedData.non_evaluator.push(partnerId);
  }

  return updatedData;
}

// PIT-03 — o assessor APROVA a continuação: prosseguir_gst='approved', registra quem
// aprovou (PIT-10: o próprio que agiu) e empurra o nível 3 (ou o gestor do promotor,
// fallback) para evaluator. Port de index.tsx:2928-2932 + 2945-2948. Muta e devolve.
export function applyAssessorApprove(
  updatedData: any,
  ctx: MadRuleContext,
  nextManagers: NextManagersV2 | null | undefined,
): any {
  updatedData.prosseguir_gst = 'approved';
  updatedData.prosseguir_who_approved = ctx.playerInfo?._id;

  if (!Array.isArray(updatedData.evaluator)) {
    updatedData.evaluator = [];
  }

  updatedData.evaluator.push(nextManagers?.nivel3?._id || ctx.playerInfo?.gestor);

  // Parceiro (gestor_2) do Nível 3 seguinte em non_evaluator, quando houver — mesma regra
  // do gestor+parceiro do pit stop. Só quando o próximo é o Nível 3 resolvido pelo backend
  // (no fallback playerInfo.gestor não há parceiro conhecido, então nada é gravado).
  const partnerId = nextManagers?.nivel3?._id ? nextManagers?.nivel3_partner?._id : undefined;

  if (partnerId) {
    if (!Array.isArray(updatedData.non_evaluator)) {
      updatedData.non_evaluator = [];
    }

    updatedData.non_evaluator.push(partnerId);
  }

  return updatedData;
}

// PIT-04 — o assessor PARA a cadeia: prosseguir_gst='stopped' e status='concluido'.
// Port de index.tsx:2742 + 2745. Muta e devolve.
export function applyAssessorStop(updatedData: any): any {
  updatedData.prosseguir_gst = 'stopped';
  updatedData.status = 'concluido';

  return updatedData;
}

// PIT-08 — reset para o estado de pit stop (janela 4.1): reabre o pit stop com
// prosseguir_gst='next' e a realização como promovido 4/step3. Port de
// index.tsx:2591-2597. Muta e devolve.
export function resetPitStop41(obj: any): any {
  obj.prosseguir_gst = 'next';
  obj.status = 'promovido';
  obj.multiplicador = 4;
  obj.stageNumber = 3;

  return obj;
}

// PIT-08 — reset para o estado de etapa 5: promovido 4/step3 (sem tocar em
// prosseguir_gst). Port de index.tsx:2598-2601. Muta e devolve.
export function resetStep5(obj: any): any {
  obj.status = 'promovido';
  obj.multiplicador = 4;
  obj.stageNumber = 3;

  return obj;
}
